import React, { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import FiltersHeader from '../../NewSearch/common/FiltersHeader';
import Button from '../../Button';
import EstateLocation from './blocks/EstateLocation';
import useNewItemLocalState from '../../../hooks/useNewItemLocalState';
import useCategoriesData from '../../../hooks/useCategoriesData';
import {
  setCreationResetButtonIsDisabled,
  toggleAuthWindow
} from '../../../actions';
import Photos from './blocks/Photos';
import AlertModal from '../../AlertModal';
import { FORM_VALUES } from '../../../utils/helpers/constants';
import CONFIG from '../../../config';
import { sendAccessToken } from '../../../queries/mutations';

const CategoriesDropDown = dynamic(() => import('./blocks/Categories'), {
  ssr: false
});

const NewSearchLeased = dynamic(
  () => import('../../NewSearch/NewSearchFilters/components/NewSearchLeased'),
  {
    ssr: false
  }
);

const Contacts = dynamic(() => import('./blocks/Contacts'), {
  ssr: false
});

const Pricing = dynamic(() => import('./blocks/Pricing'), {
  ssr: false
});

const AboutEstate = dynamic(() => import('./blocks/AboutEstate'), {
  ssr: false
});

const Description = dynamic(() => import('./blocks/Description'), {
  ssr: false
});

const resetColor = '#4264fd';

const AddNewItemForm = ({
  profile,
  errorText,
  setErrorText,
  createNewItem,
  failedModalIsOpen,
  toggleFailedModal
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { reset, submit, change } = useForm();
  const { values, errors } = useFormState();
  const [mutateToken] = useMutation(sendAccessToken);
  const [loading, setLoading] = useState(false);
  useCategoriesData({ values, change });
  const [citiesLoading, setCitiesIsLoading] = useState(false);
  const creationResetButtonIsDisabled = useSelector(
    state => state.addNewItemModal.creationResetButtonIsDisabled
  );
  const isOpenAuthWindow = useSelector(
    state => state.addNewItemModal.isOpenAuthWindow
  );
  const { sessionKey } = useNewItemLocalState();
  const errorKeys = Object.keys(errors);
  const typeEstate = values[FORM_VALUES.typeEstate];
  const agreement = t('authentication.agreement');
  const rules = t('authentication.rules');
  const isAgency = profile?.targetType === 'AGENCY';
  const returnToUrl =
    typeof window !== 'undefined' ? btoa(window.location.href) : '';
  const authUrl = `${CONFIG.authAppUrl}/phone-confirmation?phone=${
    values[FORM_VALUES.phone]
  }&return_to=${returnToUrl}`;

  const closeWindow = () => {
    dispatch(toggleAuthWindow(false));
  };

  const validateAndSubmit = async () => {
    await submit();
    if (errorKeys.length) {
      const errorField = document.querySelector('.item-modal__error-text');
      if (errorField) {
        errorField.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  };

  const handleTokenSuccess = (res, token) => {
    if (!res.data?.signIn?.errors?.length) {
      createNewItem({ accessToken: token, values, setLoading });
    } else {
      dispatch(setCreationResetButtonIsDisabled(false));
      setLoading(false);
      toggleFailedModal();
    }
  };

  const handleTokenError = reason => {
    dispatch(setCreationResetButtonIsDisabled(false));
    toggleFailedModal();
    setLoading(false);
    console.error('error in validate auth token', reason);
  };

  const handleToken = token => {
    if (!token) return;
    closeWindow();
    mutateToken({
      variables: { accessToken: token }
    }).then(res => handleTokenSuccess(res, token), handleTokenError);
  };

  const handleAuth = event => {
    if (event.data.name === 'authenticationFinished') {
      setLoading(true);
      dispatch(setCreationResetButtonIsDisabled(true));
      handleToken(event.data.access_token);
    } else if (event.data === 'authenticationModalClose') {
      closeWindow();
      setLoading(false);
      dispatch(setCreationResetButtonIsDisabled(false));
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleAuth);

    return () => window.removeEventListener('message', handleAuth);
  }, [values]);

  const formResetHandler = () => {
    let resetData = {
      [FORM_VALUES.owner]: 'OWNER',
      [FORM_VALUES.leased]: 'false',
      [FORM_VALUES.hasBillOfSale]: false,
      [FORM_VALUES.hasMortgage]: false
    };
    if (profile) {
      resetData = {
        ...resetData,
        [FORM_VALUES.company]: values[FORM_VALUES.company],
        [FORM_VALUES.phone]: values[FORM_VALUES.phone],
        [FORM_VALUES.email]: values[FORM_VALUES.email]
      };
      if (isAgency) {
        resetData[FORM_VALUES.owner] = values[FORM_VALUES.owner];
      }
    }
    reset(resetData);
  };

  const onHideHandleClick = () => {
    if (!loading) {
      if (sessionKey) {
        router.back();
      } else {
        router.push('/');
      }
    }
  };

  const clearErrorAndClose = () => {
    setErrorText('');
    toggleFailedModal();
  };

  return (
    <div className="item-modal__content">
      <FiltersHeader
        textColor={resetColor}
        title={t('new_ad.title')}
        close={onHideHandleClick}
        cypress="new-ad-popup-title"
        handleReset={formResetHandler}
        isDisabled={creationResetButtonIsDisabled}
      />
      <div className="item-modal__type-deal">
        <NewSearchLeased
          t={t}
          defaultValue="false"
          margin="15px 0 10px"
          isCreateNewItemPage
          value={values[FORM_VALUES.leased]}
        />
        <CategoriesDropDown value={typeEstate} />
      </div>
      <EstateLocation
        citiesLoading={citiesLoading}
        setCitiesIsLoading={setCitiesIsLoading}
      />
      <AboutEstate />
      <Pricing />
      <Description />
      <Photos />
      <Contacts profile={profile} />
      <AlertModal
        acceptButtonTitle="OK"
        isOpen={failedModalIsOpen}
        closeHandler={clearErrorAndClose}
        title={t('payments.errors.title')}
        onAcceptHandlerClick={clearErrorAndClose}
        message={errorText || t('common.something_went_wrong')}
      />
      <Button
        name="button"
        type="button"
        id="new-ad-form-submit-button"
        onClickHandler={validateAndSubmit}
        additionalClass="item-modal__button"
        disabled={loading || citiesLoading}
      >
        {loading ? <span className="f-loader" /> : t('new_ad.continue')}
      </Button>
      <div className="item-modal__terms">
        <Trans i18nKey="new_ad.confirmation" values={{ agreement, rules }}>
          <a href="/pages/terms-and-conditions" className="item-modal__link">
            {{ agreement }}
          </a>
          <a href="/pages/rules" className="item-modal__link">
            {{ rules }}
          </a>
        </Trans>
      </div>
      {isOpenAuthWindow && (
        <iframe title="auth" src={authUrl} className="item-modal__auth" />
      )}
    </div>
  );
};

AddNewItemForm.propTypes = {
  profile: PropTypes.object,
  errorText: PropTypes.string,
  setErrorText: PropTypes.func.isRequired,
  createNewItem: PropTypes.func.isRequired,
  toggleFailedModal: PropTypes.func.isRequired,
  failedModalIsOpen: PropTypes.bool.isRequired
};

export default AddNewItemForm;
